import { styled } from "styled-components";
import { BackButton } from "../../../components/layout/back-button/back-button.component";
import { Column } from "../../../components/ui/grid/column.component";
import { Row } from "../../../components/ui/grid/row.component";
import { Spacer } from "../../../components/ui/spacer/spacer.component";
import { Button, Spin } from "antd";
import { colors } from "../../../theme/colors";
import { MdDelete, MdEdit } from "react-icons/md";
import { Children } from "../../../types/Children";
import { Link, useNavigate } from "react-router-dom";
import { pluralizeKind } from "../../../utils/functions/string/pluralizeKind";
import _ from "lodash";
import { ProjectKind } from "../../../enums/ProjectKind";
import { kindToString } from "../../../utils/functions/string/kindToString";
import { LoadingOutlined } from "@ant-design/icons";
import { IoMdEye } from "react-icons/io";
import useModal from "antd/es/modal/useModal";
import { axios } from "../../../api/axios";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import toast from "react-hot-toast";

type ViewLayoutProps = {
  id: string;
  customUrl?: string;
  children: Children;
  kind?: ProjectKind;
  showEpisodes?: boolean;
  episodeCount?: number;
  title?: string;
  subtitle?: string;
  loading?: boolean;
  details?: string[];
  distributed?: boolean;
  status?: string;
  viewOnly?: boolean;
};

export const ViewLayout = ({
  id,
  children,
  customUrl,
  kind = ProjectKind.Film,
  showEpisodes = true,
  episodeCount,
  title,
  subtitle,
  loading,
  details = [],
  distributed,
  status,
  viewOnly = false,
}: ViewLayoutProps) => {
  const navigate = useNavigate();

  const [modal, modalConfig] = useModal();

  return (
    <Main>
      {modalConfig}
      <BackButton />
      {/* <BackgroundImage></BackgroundImage> */}
      <Overlay></Overlay>
      {loading ? (
        <Spinner
          indicator={
            <LoadingOutlined
              color={colors.primary.hex}
              style={{ fontSize: 48 }}
              spin
            />
          }
        />
      ) : (
        <Column style={{ width: "100%", zIndex: 10, alignItems: "flex-start" }}>
          <Row
            style={{
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            {kind === ProjectKind.Series && showEpisodes ? (
              <DetailsLink to={`/${pluralizeKind(kind)}/${id}/episodes`}>
                Episodes ({episodeCount ?? 0})
              </DetailsLink>
            ) : null}
            {/* <DetailsLink to={`/${pluralizeKind(kind)}/${id}/cast-crew`}>
              Cast and Crew
            </DetailsLink> */}
          </Row>
          <Spacer size={20} direction="bottom" />
          {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
          <Spacer size={-10} direction="bottom" />
          <Row
            style={{
              width: "100%",
              justifyContent: "space-between",
              flexWrap: "wrap",
              alignItems: "flex-start",
              gap: "1rem",
            }}
          >
            <Title>{title ?? "Friends Return"}</Title>
            <Row $gap={10} $wrap>
              {(!distributed || status === "draft") && !viewOnly ? (
                <Button
                  type={"text"}
                  color={colors.error.hex}
                  icon={<MdDelete />}
                  onClick={() => {
                    modal.confirm({
                      title: "Are you sure you want to delete?",
                      content:
                        "Are you sure you want to permanently delete this film? This cannot be reversed.",
                      okText: "Delete",
                      cancelText: "Cancel",
                      onOk: () => {
                        axios
                          .delete("/client/film", {
                            data: {
                              film_id: id,
                            },
                          })
                          .then((resp) => {
                            toast.success("Film deleted successfully!");
                            navigate("/");
                          })
                          .catch(handleResponse);
                      },
                      onCancel: () => null,
                    });
                  }}
                >
                  Delete
                </Button>
              ) : null}
              {!viewOnly ? (
                <>
                  <Button
                    type={"dashed"}
                    icon={<MdEdit />}
                    onClick={() =>
                      navigate(
                        kind === ProjectKind.Series
                          ? `/s/${customUrl}/edit`
                          : `/${customUrl}/edit`
                      )
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    type="primary"
                    style={{ backgroundColor: colors.primary.hex }}
                    icon={distributed ? <IoMdEye /> : <MdEdit />}
                    onClick={() =>
                      navigate(
                        `/${
                          kind === ProjectKind.Film ? "f" : "s"
                        }/${customUrl}/distribute`
                      )
                    }
                  >
                    {distributed
                      ? "View Distribution"
                      : "Submit for distribution"}
                  </Button>
                </>
              ) : null}
            </Row>
          </Row>
          <Details>
            {_.capitalize(kindToString(kind))}
            {details.map((detail, i) => (
              <span key={`Detail__${i}`}>
                &nbsp;&nbsp;&#x2022;&nbsp;&nbsp;{detail}
              </span>
            ))}
            {/* &nbsp;&nbsp;&#x2022;&nbsp;&nbsp;{year}&nbsp;&nbsp;&#x2022;&nbsp;&nbsp;78m
            12s */}
          </Details>
          <Spacer size={10} direction="bottom" />
          {children}
        </Column>
      )}
    </Main>
  );
};

const Spinner = styled(Spin)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const Main = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-height: 100vh;

  color: ${colors.text.hex};
  padding: 8.5rem clamp(1.6rem, 6vw, 10rem) clamp(2rem, 4vw, 3.5rem);
  padding-right: max(8rem, clamp(1.6rem, 6vw, 10rem));

  & .ant-tabs {
    max-width: 100%;
    min-width: 0;
  }

  & .ant-tabs-nav,
  & .ant-tabs-nav-wrap {
    max-width: 100%;
  }

  & .ant-tabs-nav-list {
    flex-wrap: wrap;
  }

  & .ant-tabs-tab-btn {
    color: ${colors.text.hex};
  }
`;

const BackgroundImage = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("/images/live/friends.png");
  background-size: cover;
  z-index: -1;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${colors.background.hex};
  backdrop-filter: blur(20px);
  z-index: -1;
`;

const DetailsLink = styled(Link)`
  font-size: 1.5rem;
  color: ${colors.white.hex};
  font-weight: 600;
  text-decoration: none;

  & span {
    transition: all 0.3s;
    display: inline-flex;
    align-items: center;
    font-weight: 500;
  }

  &:hover {
    text-decoration: underline;

    & span {
      color: ${colors.primary.hex};
    }
  }
`;

const Subtitle = styled.h2`
  font-size: 2rem;
  font-weight: 500;
  opacity: 0.7;
`;

const Title = styled.h1`
  font-size: clamp(2.4rem, 5vw, 4.5rem);
  font-weight: 500;
  overflow-wrap: anywhere;
  max-width: 100%;
`;

const Details = styled.h3`
  margin: 1.6rem 0 0;
  font-size: 1.5rem;
  font-weight: 400;
  opacity: 60%;
`;
